import { createSlice, PayloadAction } from '@reduxjs/toolkit'

interface NotificationState {
  id: string
  type: 'success' | 'error' | 'warning' | 'info'
  title: string
  message: string
  duration?: number
  actions?: Array<{
    label: string
    action: () => void
  }>
}

interface UIState {
  sidebarOpen: boolean
  mobileMenuOpen: boolean
  theme: 'light' | 'dark' | 'system'
  notifications: NotificationState[]
  loading: {
    global: boolean
    alerts: boolean
    workOrders: boolean
    equipment: boolean
    analytics: boolean
  }
  modals: {
    createWorkOrder: {
      open: boolean
      alertId?: string
    }
    equipmentDetails: {
      open: boolean
      equipmentId?: string
    }
    userProfile: {
      open: boolean
    }
    settings: {
      open: boolean
    }
  }
  filters: {
    alerts: {
      priority: string[]
      status: string[]
      equipment: string[]
      dateRange?: {
        start: string
        end: string
      }
    }
    workOrders: {
      status: string[]
      priority: string[]
      assignee: string[]
      dateRange?: {
        start: string
        end: string
      }
    }
    equipment: {
      type: string[]
      status: string[]
      health: {
        min?: number
        max?: number
      }
    }
  }
  searchQueries: {
    alerts: string
    workOrders: string
    equipment: string
  }
  pagination: {
    alerts: {
      page: number
      limit: number
      total: number
    }
    workOrders: {
      page: number
      limit: number
      total: number
    }
    equipment: {
      page: number
      limit: number
      total: number
    }
  }
}

const initialState: UIState = {
  sidebarOpen: true,
  mobileMenuOpen: false,
  theme: 'light',
  notifications: [],
  loading: {
    global: false,
    alerts: false,
    workOrders: false,
    equipment: false,
    analytics: false,
  },
  modals: {
    createWorkOrder: {
      open: false,
    },
    equipmentDetails: {
      open: false,
    },
    userProfile: {
      open: false,
    },
    settings: {
      open: false,
    },
  },
  filters: {
    alerts: {
      priority: [],
      status: [],
      equipment: [],
    },
    workOrders: {
      status: [],
      priority: [],
      assignee: [],
    },
    equipment: {
      type: [],
      status: [],
      health: {},
    },
  },
  searchQueries: {
    alerts: '',
    workOrders: '',
    equipment: '',
  },
  pagination: {
    alerts: {
      page: 1,
      limit: 20,
      total: 0,
    },
    workOrders: {
      page: 1,
      limit: 20,
      total: 0,
    },
    equipment: {
      page: 1,
      limit: 20,
      total: 0,
    },
  },
}

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    toggleSidebar: (state) => {
      state.sidebarOpen = !state.sidebarOpen
    },
    setSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.sidebarOpen = action.payload
    },
    toggleMobileMenu: (state) => {
      state.mobileMenuOpen = !state.mobileMenuOpen
    },
    setMobileMenuOpen: (state, action: PayloadAction<boolean>) => {
      state.mobileMenuOpen = action.payload
    },
    setTheme: (state, action: PayloadAction<'light' | 'dark' | 'system'>) => {
      state.theme = action.payload
    },
    addNotification: (state, action: PayloadAction<Omit<NotificationState, 'id'>>) => {
      const notification: NotificationState = {
        ...action.payload,
        id: Date.now().toString(),
      }
      state.notifications.push(notification)
    },
    removeNotification: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter(n => n.id !== action.payload)
    },
    clearNotifications: (state) => {
      state.notifications = []
    },
    setLoading: (state, action: PayloadAction<{ key: keyof UIState['loading']; value: boolean }>) => {
      state.loading[action.payload.key] = action.payload.value
    },
    openModal: (state, action: PayloadAction<{ modal: keyof UIState['modals']; data?: any }>) => {
      const modal = state.modals[action.payload.modal] as any
      modal.open = true
      if (action.payload.data) {
        Object.assign(modal, action.payload.data)
      }
    },
    closeModal: (state, action: PayloadAction<keyof UIState['modals']>) => {
      const modal = state.modals[action.payload] as any
      modal.open = false
      // Clear modal data
      Object.keys(modal).forEach(key => {
        if (key !== 'open') {
          delete modal[key]
        }
      })
    },
    setFilter: (state, action: PayloadAction<{
      section: keyof UIState['filters']
      filter: string
      value: any
    }>) => {
      const section = state.filters[action.payload.section] as any
      section[action.payload.filter] = action.payload.value
    },
    clearFilters: (state, action: PayloadAction<keyof UIState['filters']>) => {
      const section = action.payload
      if (section === 'alerts') {
        state.filters.alerts = {
          priority: [],
          status: [],
          equipment: [],
        }
      } else if (section === 'workOrders') {
        state.filters.workOrders = {
          status: [],
          priority: [],
          assignee: [],
        }
      } else if (section === 'equipment') {
        state.filters.equipment = {
          type: [],
          status: [],
          health: {},
        }
      }
    },
    setSearchQuery: (state, action: PayloadAction<{
      section: keyof UIState['searchQueries']
      query: string
    }>) => {
      state.searchQueries[action.payload.section] = action.payload.query
    },
    setPagination: (state, action: PayloadAction<{
      section: keyof UIState['pagination']
      data: Partial<UIState['pagination'][keyof UIState['pagination']]>
    }>) => {
      const section = state.pagination[action.payload.section]
      Object.assign(section, action.payload.data)
    },
  },
})

export const {
  toggleSidebar,
  setSidebarOpen,
  toggleMobileMenu,
  setMobileMenuOpen,
  setTheme,
  addNotification,
  removeNotification,
  clearNotifications,
  setLoading,
  openModal,
  closeModal,
  setFilter,
  clearFilters,
  setSearchQuery,
  setPagination,
} = uiSlice.actions

// Selectors
export const selectUI = (state: { ui: UIState }) => state.ui
export const selectSidebarOpen = (state: { ui: UIState }) => state.ui.sidebarOpen
export const selectMobileMenuOpen = (state: { ui: UIState }) => state.ui.mobileMenuOpen
export const selectTheme = (state: { ui: UIState }) => state.ui.theme
export const selectNotifications = (state: { ui: UIState }) => state.ui.notifications
export const selectLoading = (state: { ui: UIState }) => state.ui.loading
export const selectModals = (state: { ui: UIState }) => state.ui.modals
export const selectFilters = (state: { ui: UIState }) => state.ui.filters
export const selectSearchQueries = (state: { ui: UIState }) => state.ui.searchQueries
export const selectPagination = (state: { ui: UIState }) => state.ui.pagination

export default uiSlice.reducer